import { createPortal } from "react-dom";
import type { Nit } from "@/api/nits";
import { KeyValueRow } from "@/components/ui/KeyValueRow";
import { Button } from "@/components/ui/Button";
import { useFocusTrap } from "@/hooks/useFocusTrap";

interface VerNitModalProps {
  nit: Nit | null;
  onClose: () => void;
}

export const VerNitModal = ({ nit, onClose }: VerNitModalProps) => {
  const dialogRef = useFocusTrap(Boolean(nit), { onEscape: onClose });

  if (!nit) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ver-nit-title"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-elevated"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 pb-3">
          <div>
            <h2
              id="ver-nit-title"
              className="text-lg font-semibold text-ink-primary"
            >
              Detalle del NIT
            </h2>
            <p className="text-xs text-ink-secondary mt-0.5">
              Información completa del registro de tercero
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-secondary hover:bg-slate-100 hover:text-ink-primary"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 space-y-1">
          <KeyValueRow label="NIT" value={nit.nit} />
          <KeyValueRow label="DV" value={nit.dv ? String(nit.dv) : "—"} />
          <KeyValueRow
            label="Razón Social"
            value={nit.razon_social?.toUpperCase()}
          />
          <KeyValueRow label="Teléfono" value={nit.telefono} />
          <KeyValueRow
            label="Correo Electrónico"
            value={nit.correo_electronico}
          />
          <KeyValueRow label="Dirección" value={nit.direccion?.toUpperCase()} />
          <KeyValueRow label="Ciudad" value={nit.ciudad?.toUpperCase()} />
          <KeyValueRow label="País" value={nit.pais?.toUpperCase()} />
          {nit.created_at ? (
            <KeyValueRow
              label="Creado"
              value={new Date(nit.created_at).toLocaleString("es-CO")}
            />
          ) : null}
          {nit.updated_at ? (
            <KeyValueRow
              label="Actualizado"
              value={new Date(nit.updated_at).toLocaleString("es-CO")}
            />
          ) : null}
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="button" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
